import React, { useEffect } from "react";
import { useImmerReducer } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme";
import { Grid } from "@mui/material";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { useGetOrg } from "@src/api/organizations/get-organization";
import {
  AnalyticsFilterReducer,
  AnalyticsFilterReducerAction,
} from "@src/api/reducers/analyticsFilterReducer";
import { DashboardRenderFlags } from "@src/api/types/dashboards";
import { AnalyticsStickyHeaderBase } from "@src/features/analytics/analyticsHeader/AnalyticsStickyHeaderBase";
import { useGetSitewideAnalyticsCountries } from "@src/features/analytics/hooks/use-get-analytics-countries";
import { useSelector } from "@src/store";
import { getSitewidePageName } from "@src/store/slices/sitewideDashboardFilters";
import { AnalyticsActiveFilterType } from "@src/store/types/dashboardFilters";

interface AnalyticsStickyHeaderProps {
  shouldNotRenderGraphs?: DashboardRenderFlags;
}

export const SitewideAnalyticsStickyHeader = ({
  shouldNotRenderGraphs,
}: AnalyticsStickyHeaderProps) => {
  const isDataLoading = useAnalyticsFetching();
  const pageName = useSelector(getSitewidePageName);
  const organization = useGetOrg();

  const sitewideDashboardFilters = useSelector(
    (state) => state.sitewideDashboardFilters
  );
  const countryCodesData = useGetSitewideAnalyticsCountries();

  const [activeFilterArray, dispatchActiveFilterArray] = useImmerReducer<
    AnalyticsActiveFilterType[],
    AnalyticsFilterReducerAction
  >(AnalyticsFilterReducer, []);

  const filteredFilters = activeFilterArray.filter(
    (filter) => filter.key !== "startTime" && filter.key !== "endTime"
  );

  useEffect(() => {
    // dispatchActiveFilterArray({
    //   type: "updateIsZScoreEnabled",
    //   payload: { sitewideDashboardFilters },
    // });

    dispatchActiveFilterArray({
      type: "updateDeviceType",
      payload: { deviceType: sitewideDashboardFilters.deviceType },
    });
    dispatchActiveFilterArray({
      type: "updateVisitorType",
      payload: { visitorType: sitewideDashboardFilters.visitorType },
    });

    dispatchActiveFilterArray({
      type: "updateChannelType",
      payload: { channels: sitewideDashboardFilters.channels },
    });

    dispatchActiveFilterArray({
      type: "updateCountryCodes",
      payload: { countryCodes: sitewideDashboardFilters.countryCodes },
    });
    dispatchActiveFilterArray({
      type: "updateEnd",
      payload: {
        endTime: { endTime: sitewideDashboardFilters.endTime },
        timezone: organization.data.org.timezone ?? "UTC",
      },
    });
    dispatchActiveFilterArray({
      type: "updateStart",
      payload: {
        startTime: { startTime: sitewideDashboardFilters.startTime },
        timezone: organization.data.org.timezone ?? "UTC",
      },
    });
    dispatchActiveFilterArray({
      type: "updateLandingPage",
      payload: { landingPage: sitewideDashboardFilters.landingPage },
    });
  }, [
    sitewideDashboardFilters.startTime,
    sitewideDashboardFilters.endTime,
    sitewideDashboardFilters.deviceType,
    sitewideDashboardFilters.channels?.length,
    sitewideDashboardFilters.visitorType,
    sitewideDashboardFilters.countryCodes,
    sitewideDashboardFilters.landingPage,
    sitewideDashboardFilters.isZScoreEnabled,
    sitewideDashboardFilters.maxZScore,
  ]);

  return (
    <Grid container>
      <Grid item>
        <IgTypography variant={"h1"}>Sitewide Analytics</IgTypography>
      </Grid>
      <AnalyticsStickyHeaderBase
        pageName={pageName}
        isDataLoading={isDataLoading}
        renderLocation={"sitewide"}
        activeFilterArray={filteredFilters}
        shouldNotRenderGraphs={shouldNotRenderGraphs}
        filters={sitewideDashboardFilters}
        countryCodesData={countryCodesData}
      />
    </Grid>
  );
};
